// @ts-nocheck
import React, { useState } from 'react'
import { Card, Tabs, Button, Space, Spin, Alert, Row, Col, Image, Tag } from 'antd'
import MaterialIcon from './MaterialIcon'
import { useAISuggestions } from '../hooks/useAISuggestions'

interface PriceBookItem {
  id: string
  name: string
  description?: string
  unitPrice: number
  unit?: string
  category?: string
  images?: string[]
}

interface AIResponseSuggestionsProps {
  threadId: string
  messageId: string
  onSelectResponse: (response: string) => void
  onAddProducts?: (products: PriceBookItem[]) => void
  onClose: () => void
}

const AIResponseSuggestions: React.FC<AIResponseSuggestionsProps> = ({
  threadId,
  messageId,
  onSelectResponse,
  onAddProducts,
  onClose,
}) => {
  const { suggestions, isLoading, error, isEnabled } = useAISuggestions(threadId, messageId)
  const [selectedTone, setSelectedTone] = useState<string>('friendly')
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])

  if (!isEnabled) {
    return (
      <Card style={{ marginBottom: '24px' }}>
        <Alert
          message="AI Features Disabled"
          description="AI response suggestions are not enabled. Contact your administrator to enable AI features."
          type="info"
          showIcon
        />
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>
            AI is crafting the perfect response...
          </div>
        </div>
      </Card>
    )
  }

  if (error) {
    return (
      <Card style={{ marginBottom: '24px' }}>
        <Alert
          message="AI Error"
          description="Failed to generate AI suggestions. Please try again or write your response manually."
          type="error"
          showIcon
          closable
          onClose={onClose}
        />
      </Card>
    )
  }

  if (!suggestions) {
    return null
  }

  const handleUseResponse = () => {
    const response = suggestions[selectedTone as keyof typeof suggestions]
    onSelectResponse(response)
  }

  const handleToggleProduct = (productId: string) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    )
  }

  const handleAddProducts = () => {
    if (onAddProducts && suggestions.recommendedProducts) {
      const productsToAdd = suggestions.recommendedProducts
        .filter((p: PriceBookItem) => selectedProducts.includes(p.id))
        .map((p: PriceBookItem) => ({ ...p, quantity: 1, subtotal: p.unitPrice }))
      onAddProducts(productsToAdd)
    }
  }

  const recommendedProducts = suggestions?.recommendedProducts || []
  const hasRecommendations = recommendedProducts.length > 0

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      {/* Product Recommendations Section */}
      {hasRecommendations && (
        <Card
          title={
            <Space>
              <MaterialIcon icon="star" style={{ color: '#faad14', fontSize: '20px' }} />
              <span>Recommended Products</span>
              <Tag color="blue">AI Suggested</Tag>
            </Space>
          }
          extra={
            selectedProducts.length > 0 && (
              <Button
                type="primary"
                icon={<MaterialIcon icon="add_shopping_cart" style={{ fontSize: '18px' }} />}
                onClick={handleAddProducts}
              >
                Add {selectedProducts.length} to Email
              </Button>
            )
          }
        >
          <div style={{ marginBottom: '12px', color: '#666' }}>
            💡 Based on the customer's inquiry, these products might be relevant:
          </div>
          <Row gutter={[16, 16]}>
            {recommendedProducts.map((product: PriceBookItem) => {
              const isSelected = selectedProducts.includes(product.id)
              const primaryImage = product.images?.[0]

              return (
                <Col xs={24} sm={12} md={8} key={product.id}>
                  <Card
                    hoverable
                    onClick={() => handleToggleProduct(product.id)}
                    style={{
                      border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      background: isSelected ? '#f0f7ff' : 'white',
                      transition: 'all 0.3s',
                    }}
                  >
                    {primaryImage ? (
                      <Image
                        src={primaryImage}
                        alt={product.name}
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                        }}
                        preview={false}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100%',
                          height: '120px',
                          background: '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                        }}
                      >
                        <MaterialIcon icon="inventory_2" style={{ fontSize: '48px', color: '#bbb' }} />
                      </div>
                    )}
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '4px' }}>
                        {isSelected && (
                          <MaterialIcon icon="check_circle" style={{ color: '#1890ff', marginRight: '4px' }} />
                        )}
                        {product.name}
                      </div>
                      {product.description && (
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                          {product.description.length > 60
                            ? product.description.substring(0, 60) + '...'
                            : product.description}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontWeight: 600, color: '#1890ff', fontSize: '16px' }}>
                          ${product.unitPrice.toFixed(2)}
                          {product.unit && <span style={{ fontSize: '12px', color: '#666' }}>/{product.unit}</span>}
                        </div>
                        {product.category && (
                          <Tag color="blue" style={{ fontSize: '11px' }}>{product.category}</Tag>
                        )}
                      </div>
                    </div>
                  </Card>
                </Col>
              )
            })}
          </Row>
        </Card>
      )}

      {/* AI Response Suggestions */}
      <Card
        title={
          <Space>
            <MaterialIcon icon="auto_awesome" style={{ color: '#1890ff', fontSize: '20px' }} />
            <span>AI Response Suggestions</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="primary"
              icon={<MaterialIcon icon="check" style={{ fontSize: '18px' }} />}
              onClick={handleUseResponse}
            >
              Use This Response
            </Button>
            <Button icon={<MaterialIcon icon="close" style={{ fontSize: '18px' }} />} onClick={onClose}>
              Close
            </Button>
          </Space>
        }
        style={{ marginBottom: '24px' }}
      >
      <Tabs
        activeKey={selectedTone}
        onChange={setSelectedTone}
        items={[
          {
            key: 'friendly',
            label: '😊 Friendly',
            children: (
              <div style={{
                whiteSpace: 'pre-wrap',
                padding: '16px',
                background: '#f6ffed',
                borderRadius: '8px',
                border: '1px solid #b7eb8f',
              }}>
                {suggestions.friendly}
              </div>
            ),
          },
          {
            key: 'professional',
            label: '👔 Professional',
            children: (
              <div style={{
                whiteSpace: 'pre-wrap',
                padding: '16px',
                background: '#e6f7ff',
                borderRadius: '8px',
                border: '1px solid #91d5ff',
              }}>
                {suggestions.professional}
              </div>
            ),
          },
          {
            key: 'quick',
            label: '⚡ Quick',
            children: (
              <div style={{
                whiteSpace: 'pre-wrap',
                padding: '16px',
                background: '#fff7e6',
                borderRadius: '8px',
                border: '1px solid #ffd591',
              }}>
                {suggestions.quick}
              </div>
            ),
          },
        ]}
      />

        <div style={{ marginTop: '16px', fontSize: '12px', color: '#999' }}>
          💡 Tip: AI suggestions are a starting point. Review and personalize before sending!
        </div>
      </Card>
    </Space>
  )
}

export default AIResponseSuggestions

